<template>
    <div class="container-fluid">
        <!-- Header Section -->
        <div class="row mb-4">
            <div class="col-12">
                <h4 class="mb-3">Real-time Supervision Status</h4>
            </div>
        </div>

        <!-- Key Metrics Section -->
        <div class="row mb-4" v-if="!isFullscreen">
            <div class="col-md-3">
                <div class="card bg-primary text-white">
                    <div class="card-body">
                        <h6 class="card-title">Total Clients</h6>
                        <h2 class="mb-0">{{ totalClients }}</h2>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="card bg-warning text-white">
                    <div class="card-body">
                        <h6 class="card-title">High Risk Clients</h6>
                        <h2 class="mb-0">{{ highRiskClients }}</h2>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="card bg-danger text-white">
                    <div class="card-body">
                        <h6 class="card-title">FTA Alerts</h6>
                        <h2 class="mb-0">{{ ftaAlerts }}</h2>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="card bg-info text-white">
                    <div class="card-body">
                        <h6 class="card-title">FTP Alerts</h6>
                        <h2 class="mb-0">{{ ftpAlerts }}</h2>
                    </div>
                </div>
            </div>
        </div>

        <!-- Map Section -->
        <div class="row mb-4">
            <div class="col-12">
                <div class="card">
                    <div class="card-header bg-white">
                        <div class="d-flex justify-content-between align-items-center">
                            <h5 class="mb-0">Geolocation Monitoring</h5>
                            <div class="btn-group" v-if="currentClient && currentClient.uuid">
                                <button class="btn btn-outline-secondary" @click="toggleFullscreen">
                                    <i class="fas" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
                                </button>
                                <button class="btn btn-outline-primary" @click="stepBackward" :disabled="currentTimeIndex === 0">
                                    <i class="fas fa-step-backward"></i>
                                </button>
                                <button class="btn btn-outline-primary" @click="togglePlayback">
                                    <i class="fas" :class="isPlaying ? 'fa-pause' : 'fa-play'"></i>
                                </button>
                                <button class="btn btn-outline-primary" @click="stepForward" :disabled="currentTimeIndex === timelineData.length - 1">
                                    <i class="fas fa-step-forward"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="card-body p-0">
                        <div id="supervision-map" 
                             class="map-lg" 
                             :class="{ 'map-fullscreen': isFullscreen }"
                             @click="focusMap">
                            <button v-if="isFullscreen" class="btn btn-light exit-fullscreen-btn" @click.stop="toggleFullscreen">
                                <i class="fas fa-compress"></i> Exit Fullscreen
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Client List Section -->
        <div class="row" v-if="!isFullscreen">
            <div class="col-12">

                        <template v-if="currentClient && currentClient.uuid">
                            <div class="card">
                                <div class="card-header bg-white d-flex justify-content-between align-items-center">
                                    <button class="btn btn-outline-secondary" @click="toggleClient(currentClient)">
                                        <i class="fas fa-arrow-left me-2"></i>Back to List
                                    </button>
                                    <div class="d-flex align-items-center">
                                        <span class="badge me-2" :class="getSupervisionLevelClass(currentClient.supervisionLevel)">
                                            {{ currentClient.supervisionLevel }}
                                        </span>
                                        <span class="badge" :class="getStatusClass(currentClient.status)">
                                            {{ currentClient.status }}
                                        </span>
                                    </div>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <!-- Client Photo and Basic Info -->
                                        <div class="col-md-3">
                                            <div class="text-center mb-4">
                                                <div class="mugshot-placeholder mb-3">
                                                    <div class="height-strip"></div>
                                                    <div class="placeholder-content">
                                                        <i class="fas fa-user"></i>
                                                    </div>
                                                </div>
                                                <h4 class="mb-1">{{ currentClient.name }}</h4>
                                                <p class="text-muted mb-0">Client ID: {{ currentClient.client_reference }}</p>

                                                <a :href="'/clients/'+currentClient.uuid" class="btn btn-outline-primary" target="_blank">
                                                    <i class="fa fa-edit"></i> Edit Client
                                                </a>

                                            </div>

                                            <!-- Communication Actions -->
                                            <div class="d-grid gap-2 mb-4">
                                                <button class="btn btn-outline-primary" @click="startVideoCall">
                                                    <i class="fas fa-video me-2"></i>Start Video Call
                                                </button>
                                                <button class="btn btn-outline-success" @click="makePhoneCall">
                                                    <i class="fas fa-phone me-2"></i>Make Phone Call
                                                </button>
                                                <button class="btn btn-outline-info" @click="sendMessage">
                                                    <i class="fas fa-comment me-2"></i>Send Message
                                                </button>
                                                    <div class="btn-group">
                                                        <button type="button" class="btn btn-outline-info dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                                                            <i class="fas fa-calendar-plus me-2"></i>Schedule…
                                                        </button>
                                                        <ul class="dropdown-menu">
                                                            <li><a class="dropdown-item" href="#" @click.prevent="scheduleOfficeVisit"><i class="fas fa-building me-2"></i>Office Visit</a></li>
                                                            <li><a class="dropdown-item" href="#" @click.prevent="scheduleDrugTest"><i class="fas fa-vial me-2"></i>Drug Test</a></li>
                                                            <li><a class="dropdown-item" href="#" @click.prevent="scheduleVirtualVisit"><i class="fas fa-video me-2"></i>Virtual Meeting</a></li>
                                                        </ul>
                                                    </div>
                                            </div>

                                            <client-contacts
                                                :client_uuid="currentClient.uuid"
                                            />
                                        </div>

                                        <!-- Risk Assessment and Compliance -->
                                        <div class="col-md-9">

                                            <!-- Risk Assessment -->
                                            <div class="row mb-4">
                                                <div class="col-12">
                                                    <div class="card">
                                                        <div class="card-header bg-light">
                                                            <h5 class="mb-0">Risk Assessment</h5>
                                                        </div>
                                                        <div class="card-body">
                                                            <div class="row g-4">
                                                                <div class="col-md-4">
                                                                    <div class="score-card text-center">
                                                                        <h6 class="text-muted mb-2">Risk Score</h6>
                                                                        <div class="score-bubble" :class="getRiskClass(currentClient.riskScore)">
                                                                            {{ currentClient.riskScore }}
                                                                        </div>
                                                                        <div class="score-description mt-2">
                                                                            Overall Risk
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div class="col-md-4">
                                                                    <div class="score-card text-center">
                                                                        <h6 class="text-muted mb-2">FTA Score</h6>
                                                                        <div class="score-bubble" :class="getScoreClass(currentClient.ftaScore)">
                                                                            {{ currentClient.ftaScore }}
                                                                        </div>
                                                                        <div class="score-description mt-2">
                                                                            Failure to Appear Risk
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div class="col-md-4">
                                                                    <div class="score-card text-center">
                                                                        <h6 class="text-muted mb-2">FTP Score</h6>
                                                                        <div class="score-bubble" :class="getScoreClass(currentClient.ftpScore)">
                                                                            {{ currentClient.ftpScore }}
                                                                        </div>
                                                                        <div class="score-description mt-2">
                                                                            Failure to Pay Risk
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div class="row mt-4">
                                                                <div class="col-12">
                                                                    <div class="sentiment-card text-center">
                                                                        <h6 class="text-muted mb-2">Client Sentiment</h6>
                                                                        <div class="sentiment-display" :class="getSentimentClass(currentClient.sentiment)">
                                                                            <span class="sentiment-emoji">{{ getSentimentEmoji(currentClient.sentiment) }}</span>
                                                                            <span class="sentiment-text">{{ getSentimentText(currentClient.sentiment) }}</span>
                                                                        </div>
                                                                    </div>
                                                                </div><!--Col-->
                                                            </div><!--Row-->
                                                        </div><!--Card Body-->
                                                    </div><!--Card-->
                                                </div><!--Col-->
                                            </div>

                                            <!-- Demographics and Restrictions -->
                                            <div class="row mb-4">
                                                <div class="col-md-4">
                                                    <div class="card">
                                                        <div class="card-header">
                                                            <h6 class="mb-0"><i class="fa fa-user"></i> Demographics</h6>
                                                        </div>
                                                        <div class="card-body">
                                                            <dl class="row mb-0">
                                                                <dt class="col-sm-4">Date of Birth</dt>
                                                                <dd class="col-sm-8">{{ formatDate(currentClient.dob) }}</dd>
                                                                <dt class="col-sm-4">Gender</dt>
                                                                <dd class="col-sm-8">{{ currentClient.gender }}</dd>
                                                                <dt class="col-sm-4">Race</dt>
                                                                <dd class="col-sm-8">{{ currentClient.race }}</dd>
                                                                <dt class="col-sm-4">Ethnicity</dt>
                                                                <dd class="col-sm-8">{{ currentClient.ethnicity }}</dd>
                                                            </dl>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="col-md-4">
                                                    <div class="card">
                                                        <div class="card-header">
                                                            <h6 class="mb-0"><i class="fa fa-ban"></i> Restrictions</h6>
                                                        </div>
                                                        <div class="card-body">
                                                            <dl class="row mb-0">
                                                                <dt class="col-sm-4">Curfew</dt>
                                                                <dd class="col-sm-8">{{ currentClient.curfew || 'None' }}</dd>
                                                                <dt class="col-sm-4">Alcohol</dt>
                                                                <dd class="col-sm-8">{{ currentClient.alcohol_restriction ? 'Restricted' : 'None' }}</dd>
                                                                <dt class="col-sm-4">Drugs</dt>
                                                                <dd class="col-sm-8">{{ currentClient.drug_restriction ? 'Restricted' : 'None' }}</dd>
                                                                <dt class="col-sm-4">Travel</dt>
                                                                <dd class="col-sm-8">{{ currentClient.travel_restriction ? 'Restricted' : 'None' }}</dd>
                                                            </dl>
                                                        </div><!--Body-->
                                                    </div><!--Card-->
                                                </div><!--Col-->


                                                <div class="col-md-4">
                                                    <div class="card">
                                                        <div class="card-header">
                                                            <h6 class="mb-0"><i class="fa fa-medkit"></i> Medical</h6>
                                                        </div>
                                                        <div class="card-body">
                                                            <dl class="row mb-0">
                                                                <dt class="col-sm-4">Conditions</dt>
                                                                <dd class="col-sm-8">
                                                                    <ul class="mb-1">
                                                                        <li v-for="cond in fakeMedical.conditions" :key="cond">{{ cond }}</li>
                                                                    </ul>
                                                                </dd>
                                                                <dt class="col-sm-4">Medications</dt>
                                                                <dd class="col-sm-8">
                                                                    <ul class="mb-1">
                                                                        <li v-for="med in fakeMedical.medications" :key="med">{{ med }}</li>
                                                                    </ul>
                                                                </dd>
                                                            </dl>
                                                        </div>
                                                    </div>
                                                </div>


                                            </div><!--Row-->

                                            <!-- After the Demographics/Restrictions/Medical row -->
                                            <div class="row mb-4" v-if="currentClient && currentClient.uuid">
                                                <div class="col-12">
                                                    <div class="card">
                                                        <div class="card-header bg-light">
                                                            <h6 class="mb-0">Network / Gang Affiliations</h6>
                                                        </div>
                                                        <div class="card-body">
                                                            <ul class="list-unstyled mb-0">
                                                                <li class="mb-2">
                                                                    <i class="fas fa-users text-danger me-2"></i>
                                                                    <strong>Affiliation:</strong> Sinaloa Cartel
                                                                </li>
                                                                <li class="mb-2">
                                                                    <i class="fas fa-user-friends text-primary me-2"></i>
                                                                    <strong>Known Associate:</strong> Jose Garcia (Client #1023)
                                                                </li>
                                                                <li class="mb-2">
                                                                    <i class="fas fa-user-friends text-primary me-2"></i>
                                                                    <strong>Known Associate:</strong> Maria Lopez (Client #1045)
                                                                </li>
                                                                <li class="mb-2">
                                                                    <i class="fas fa-users text-warning me-2"></i>
                                                                    <strong>Affiliation:</strong> Jalisco New Generation Cartel
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Recent Activity -->
                                            <div class="card">
                                                <div class="card-header">
                                                    <h6 class="mb-0">Recent Activity</h6>
                                                </div>
                                                <div class="card-body">
                                                    <div class="activity-timeline">
                                                        <div v-for="(activity, idx) in (currentClient.recent_activity && currentClient.recent_activity.length ? currentClient.recent_activity : demoRecentActivity)" :key="idx" class="timeline-item">
                                                            <div class="timeline-dot" :class="{
                                                                'bg-primary': activity.type === 'Selfie Check-in',
                                                                'bg-warning': activity.type === 'Intoxication Game',
                                                                'bg-info': activity.type === 'Drug Test',
                                                                'bg-secondary': !['Selfie Check-in','Intoxication Game','Drug Test'].includes(activity.type)
                                                            }">
                                                                <i v-if="activity.type === 'Selfie Check-in'" class="fas fa-user-circle"></i>
                                                                <i v-else-if="activity.type === 'Intoxication Game'" class="fas fa-gamepad"></i>
                                                                <i v-else-if="activity.type === 'Drug Test'" class="fas fa-vial"></i>
                                                                <i v-else class="fas fa-circle"></i>
                                                            </div>
                                                            <div class="timeline-content">
                                                                <div class="d-flex justify-content-between align-items-center mb-1">
                                                                    <div>
                                                                        <strong>{{ activity.type }}</strong>
                                                                        <span class="text-muted ms-2">{{ formatDate(activity.date) }}</span>
                                                                    </div>
                                                                    <span class="badge" :class="getStatusClass(activity.status)">{{ activity.status }}</span>
                                                                </div>
                                                                <div class="mb-1">
                                                                    <i class="fas fa-map-marker-alt me-1 text-muted"></i>{{ activity.location }}
                                                                </div>
                                                                <div v-if="activity.type === 'Selfie Check-in'" class="timeline-extra">
                                                                    <p class="mb-0"><strong>Video:</strong> Verified</p>
                                                                    <div class="alert alert-danger">
                                                                       <strong>Analysis:</strong> {{ activity.analysis }}
                                                                    </div>
                                                                    <template v-if="portal && portal.link">
                                                                        <a :href="'https://'+portal.link+'.'+portal.domain+'/supervisions'" class="btn btn-outline-primary" target="_blank">
                                                                            <i class="fas fa-external-link-alt"></i> View Video
                                                                        </a>
                                                                    </template>
                                                                </div>
                                                                <div v-else-if="activity.type === 'Intoxication Game'" class="timeline-extra">
                                                                    <p class="mb-0"><strong>Game Score:</strong> {{ activity.gameScore }} / 100</p>
                                                                    <p class="mb-0"><strong>Game Name:</strong> Drive Straight</p>
                                                                    <template v-if="portal && portal.link">
                                                                        <a :href="'https://'+portal.link+'.'+portal.domain+'/supervisions/tests'" class="btn btn-outline-primary" target="_blank">
                                                                            <i class="fas fa-external-link-alt"></i> View Game
                                                                        </a>
                                                                    </template>
                                                                </div>
                                                                <div v-else-if="activity.type === 'Drug Test'" class="timeline-extra">
                                                                    <p class="mb-0"><strong>Result:</strong> {{ activity.testResult }}</p>

                                                                    <a href="#" class="btn btn-outline-primary">
                                                                        <i class="fas fa-download"></i> Download Results
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <message-feed
                                                :client_uuid="currentClient.uuid"
                                            />
                                        </div><!--Col-->
                                    </div>
                                </div>
                            </div>
                        </template>

                        <template v-if="!currentClient || !currentClient.uuid">
                            <div class="table-responsive table-striped">

                            <div class="card">
                            <div class="card-header bg-white">
                                <div class="d-flex justify-content-between align-items-center">
                                    <h5 class="mb-0">Clients in Supervision</h5>
                                    <div class="input-group" style="width: 300px;">
                                        <input type="text" class="form-control" placeholder="Search clients..." v-model="searchQuery">
                                        <button class="btn btn-outline-secondary" type="button">
                                            <i class="fas fa-search"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <table class="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Supervision Level</th>
                                        <th>Risk Score</th>
                                        <th>Next Event</th>
                                        <th>Last Location</th>
                                        <th>Outstanding Payments</th>
                                        <th>Next Check-in</th>
                                        <th>Last Check-in</th>
                                        <th>Curfew</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="client in filteredClients">
                                        <td>
                                            <button class="btn btn-link" @click="toggleClient(client)">
                                                {{ client.name }}
                                            </button>
                                        </td>
                                        <td>
                                            <span class="badge" :class="getSupervisionLevelClass(client.supervisionLevel)">
                                                {{ client.supervisionLevel }}
                                            </span>
                                        </td>
                                        <td>
                                            <div class="progress" style="height: 20px;">
                                                <div class="progress-bar" 
                                                     :class="getRiskClass(client.riskScore)"
                                                     :style="{ width: client.riskScore + '%' }">
                                                    {{ client.riskScore }}%
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            Monday 10:00 AM at Court Room A
                                        </td>
                                        <td>
                                            Inside Home
                                        </td>
                                        <td>
                                            $100.00
                                        </td>
                                        <td>
                                            Tomorrow 10:00 AM
                                        </td>
                                        <td>
                                            Yesterday 10:00 AM
                                        </td>
                                        <td>
                                            9pm - 6am
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>



    </div>
</template>

<script>
import queries from '../mixins/queries';

export default {
    mixins: [queries],
    
    data() {
        return {
            processing: false,
            lastUpdated: new Date().toLocaleString(),
            searchQuery: '',
            portal: {},
            clients: [],
            currentClient: null,
            supervisions: [],
            totalClients: 0,
            highRiskClients: 0,
            ftaAlerts: 0,
            ftpAlerts: 0,
            map: null,
            showZones: true,
            showCheckins: true,
            zones: [],
            markers: null,
            isFullscreen: false,
            mapFocused: false,
            // Timeline controls
            isPlaying: false,
            currentTimeIndex: 0,
            playbackInterval: null,
            playbackSpeed: 1000, // milliseconds between updates
            timelineData: [],
            // Add a demo recent activity list for the current client
            demoRecentActivity: [
                {
                    date: new Date(Date.now() - 1000 * 60 * 60 * 2),
                    type: 'Selfie Check-in',
                    location: 'Home',
                    status: 'NON-COMPLIANT',
                    analysis: 'Outside of the home, possible drug use'
                },
                {
                    date: new Date(Date.now() - 1000 * 60 * 60 * 5),
                    type: 'Intoxication Game',
                    location: 'Mobile App',
                    status: 'COMPLIANT',
                    gameScore: 92
                },
                {
                    date: new Date(Date.now() - 1000 * 60 * 60 * 24),
                    type: 'Drug Test',
                    location: 'Office',
                    status: 'COMPLIANT',
                    testResult: 'Negative (Urine Analysis)'
                }
            ],
            fakeMedical: {
                conditions: [
                    'Hypertension',
                    'Asthma',
                    'Type 2 Diabetes'
                ],
                medications: [
                    'Lisinopril 10mg daily',
                    'Albuterol Inhaler as needed',
                    'Metformin 500mg twice daily'
                ]
            },
        }
    },

    computed: {
        filteredClients() {
            if (!this.searchQuery) return this.clients;
            const query = this.searchQuery.toLowerCase();
            return this.clients.filter(client => 
                client.name.toLowerCase().includes(query) ||
                client.supervisionLevel.toLowerCase().includes(query)
            );
        }
    },

    watch: {
        currentClient(newClient) {
            if (newClient) {
                this.initializeTimeline();
            } else {
                this.clearTimeline();
            }
        }
    },

    mounted() {
        // Initialize map after a short delay to ensure DOM is ready
        this.$nextTick(() => {
            setTimeout(() => {
                this.initMap();
            }, 100);
        });
        this.getSupervisions();
        this.getGeolocations();
        this.getPortal();
    },

    beforeDestroy() {
        if (this.map) {
            this.map.remove();
        }
    },

    methods: {
        initMap() {
            // Check if map container exists
            const mapContainer = document.getElementById('supervision-map');
            if (!mapContainer) {
                console.error('Map container not found');
                return;
            }

            // Center coordinates for continental USA (approximately Kansas)
            const usaCenter = [39.8283, -98.5795];
            
            try {
                // Create map with dark theme
                this.map = window.L.map('supervision-map', {
                    zoomControl: true,
                    scrollWheelZoom: false,
                    dragging: true,
                    doubleClickZoom: true,
                    attributionControl: false,
                    zoom: 4,
                    minZoom: 3,
                    maxZoom: 18
                }).setView(usaCenter, 4);

                // Initialize marker cluster group first
                this.markers = window.L.markerClusterGroup({
                    spiderfyOnMaxZoom: true,
                    showCoverageOnHover: true,
                    zoomToBoundsOnClick: true,
                    maxClusterRadius: 50,
                    iconCreateFunction: function(cluster) {
                        return window.L.divIcon({
                            html: `<div class="cluster-marker">${cluster.getChildCount()}</div>`,
                            className: 'custom-cluster',
                            iconSize: window.L.point(40, 40)
                        });
                    }
                });

                // Add satellite layer as default
                const satelliteLayer = window.L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                });

                // Add road map layer
                const roadLayer = window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                });

                // Add dark base layer (not visible by default)
                const darkLayer = window.L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png', {
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                });

                // Add labels layer (not visible by default)
                const labelsLayer = window.L.tileLayer('https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png', {
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                });

                // Add terrain layer (not visible by default)
                const terrainLayer = window.L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
                    attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)'
                });

                // Add default layer
                satelliteLayer.addTo(this.map);

                // Create layer control
                const baseLayers = {
                    "Satellite": satelliteLayer,
                    "Road Map": roadLayer,
                    "Dark Mode": darkLayer,
                    "Labels": labelsLayer,
                    "Terrain": terrainLayer
                };

                window.L.control.layers(baseLayers, null, {
                    position: 'topright',
                    collapsed: false
                }).addTo(this.map);

                // Add custom zoom control
                window.L.control.zoom({
                    position: 'topright',
                    zoomInText: '<i class="fas fa-plus"></i>',
                    zoomOutText: '<i class="fas fa-minus"></i>'
                }).addTo(this.map);

                // Add scale control with imperial units
                window.L.control.scale({
                    position: 'bottomleft',
                    imperial: true,
                    metric: false,
                    maxWidth: 200
                }).addTo(this.map);

                // Add marker cluster group to map
                this.map.addLayer(this.markers);

            } catch (error) {
                console.error('Error initializing map:', error);
            }
        },

        toggleZones() {
            this.showZones = !this.showZones;
            this.updateZones();
        },

        toggleCheckins() {
            this.showCheckins = !this.showCheckins;
            this.updateCheckins();
        },

        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
            if (this.map) {
                // Force reflow
                const mapContainer = document.getElementById('supervision-map');
                if (mapContainer) {
                    mapContainer.style.display = 'none';
                    // Force reflow
                    void mapContainer.offsetWidth;
                    mapContainer.style.display = 'block';
                }
                this.map.invalidateSize();
                setTimeout(() => {
                    this.map.invalidateSize();
                }, 500);
            }
        },

        focusMap() {
            if (!this.mapFocused) {
                this.map.scrollWheelZoom.enable();
                this.mapFocused = true;
            }
        },

        blurMap() {
            if (this.mapFocused) {
                this.map.scrollWheelZoom.disable();
                this.mapFocused = false;
            }
        },

        toggleClient(client) {
            if(this.currentClient && this.currentClient.uuid === client.uuid) {
                this.currentClient = null;
            } else {
                this.currentClient = client;
            }
        },

        getGeolocations() {
            this.$http.get('/geolocations')
            .then(response => {
                if(response && response.data) {
                    this.geolocations = response.data;
                    this.updateCheckins();
                }
            })
        },

        updateZones() {
            if (!this.showZones) {
                this.zones.forEach(zone => {
                    if (this.map.hasLayer(zone)) {
                        this.map.removeLayer(zone);
                    }
                });
                return;
            }

            // Add inclusion zones (green)
            this.zones.forEach(zone => {
                if (zone.type === 'inclusion') {
                    const circle = window.L.circle([zone.lat, zone.lng], {
                        color: 'green',
                        fillColor: '#4CAF50',
                        fillOpacity: 0.2,
                        radius: zone.radius
                    }).addTo(this.map);
                    circle.bindPopup(`Inclusion Zone: ${zone.name}`);
                }
            });

            // Add exclusion zones (red)
            this.zones.forEach(zone => {
                if (zone.type === 'exclusion') {
                    const circle = window.L.circle([zone.lat, zone.lng], {
                        color: 'red',
                        fillColor: '#f44336',
                        fillOpacity: 0.2,
                        radius: zone.radius
                    }).addTo(this.map);
                    circle.bindPopup(`Exclusion Zone: ${zone.name}`);
                }
            });
        },

        updateCheckins() {
            if (!this.showCheckins) {
                if (this.markers) {
                    this.markers.clearLayers();
                }
                return;
            }

            if (!this.geolocations) return;

            // Clear existing markers
            if (this.markers) {
                this.markers.clearLayers();
            }

            this.geolocations.forEach(geo => {
                if (!geo.geo_data) return;

                let radius = 328; // Default radius in feet (100m)
                if (geo.geo_data.accuracy && geo.geo_data.accuracy < 1000) { // Only use accuracy if it's reasonable
                    radius = geo.geo_data.accuracy * 3.28084; // Convert meters to feet
                }

                const circle = window.L.circle([geo.geo_data.latitude, geo.geo_data.longitude], {
                    color: '#007AFF',
                    fillColor: '#007AFF',
                    fillOpacity: 0.15,
                    radius: radius,
                    weight: 1,
                    className: 'find-my-circle'
                });

                // Add circle directly to map
                circle.addTo(this.map);

                const popup = window.L.popup({
                    maxWidth: 300,
                    className: 'custom-popup'
                }).setContent(`
                    <div class="p-2">
                        <strong>Check-in Details</strong><br>
                        <small class="text-muted">${this.formatDate(geo.geo_data.created_at)}</small><br>
                        <span class="badge ${this.getZoneStatus(geo.geo_data).includes('danger') ? 'bg-danger' : 
                                          this.getZoneStatus(geo.geo_data).includes('success') ? 'bg-success' : 'bg-warning'}">
                            ${this.getZoneStatus(geo.geo_data).replace(/<[^>]*>/g, '')}
                        </span>
                        ${geo.geo_data.accuracy ? `<br><small class="text-muted">Accuracy: ${Math.round(geo.geo_data.accuracy * 3.28084)} feet</small>` : ''}
                    </div>
                `);

                circle.bindPopup(popup);
            });

            // if (this.geolocations.length > 0) {
            //     const bounds = this.geolocations
            //         .filter(geo => geo.geo_data)
            //         .map(geo => [geo.geo_data.latitude, geo.geo_data.longitude]);
            //     this.map.fitBounds(bounds);
            // }
        },

        getZoneStatus(location) {

            if(!this.zones) return '<span class="text-warning">No Zones</span>';

            const inExclusionZone = this.zones.some(zone => 
                zone.type === 'exclusion' && 
                this.calculateDistance(location.lat, location.lng, zone.lat, zone.lng) <= zone.radius
            );

            const inInclusionZone = this.zones.some(zone => 
                zone.type === 'inclusion' && 
                this.calculateDistance(location.lat, location.lng, zone.lat, zone.lng) <= zone.radius
            );

            if (inExclusionZone) return '<span class="text-danger">In Exclusion Zone</span>';
            if (inInclusionZone) return '<span class="text-success">In Inclusion Zone</span>';
            return '<span class="text-warning">Outside Zones</span>';
        },

        calculateDistance(lat1, lon1, lat2, lon2) {
            const R = 6371; // Earth's radius in km
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLon = (lon2 - lon1) * Math.PI / 180;
            const a = 
                Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
                Math.sin(dLon/2) * Math.sin(dLon/2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            return R * c * 1000; // Distance in meters
        },

        loadDashboardData() {
            // this.getClients();
        },

        refreshData() {
            // this.loadDashboardData();
        },

        exportReport() {
            // Export functionality would go here
        },

        setRiskScore(client) {
            client.riskScore = Math.floor(Math.random() * 100);
        },

        setFtaScore(client) {
            client.ftaScore = Math.floor(Math.random() * 100);
        },

        setFtpScore(client) {
            client.ftpScore = Math.floor(Math.random() * 100);
        },

        setSentiment(client) {
            const sentiments = ['very_positive', 'positive', 'neutral', 'negative', 'very_negative'];
            client.sentiment = sentiments[Math.floor(Math.random() * sentiments.length)];
        },

        getSupervisionLevelClass(level) {
            const classes = {
                'HIGH': 'bg-danger',
                'MEDIUM': 'bg-warning',
                'LOW': 'bg-success'
            };
            return classes[level] || 'bg-secondary';
        },

        getRiskClass(score) {
            if (score >= 80) return 'bg-danger';
            if (score >= 50) return 'bg-warning';
            return 'bg-success';
        },

        getScoreClass(score) {
            if (score >= 80) return 'bg-danger';
            if (score >= 50) return 'bg-warning';
            return 'bg-success';
        },

        getStatusClass(status) {
            const classes = {
                'COMPLIANT': 'bg-success',
                'WARNING': 'bg-warning',
                'NON-COMPLIANT': 'bg-danger',
                'PENDING': 'bg-info'
            };
            return classes[status] || 'bg-secondary';
        },

        isCheckinOverdue(date) {
            return new Date(date) < new Date();
        },

        formatDate(date) {
            return new Date(date).toLocaleDateString();
        },

        getClients(supervision) {
            this.$http.get('/supervisions/'+supervision.uuid+'/clients')
            .then(response => {
                if(response && response.data) {

                    if(response.data.length > 0) {
                        response.data.forEach(client => {
                            client.supervisionLevel = supervision.name;
                            this.setRiskScore(client);
                            this.setFtaScore(client);
                            this.setFtpScore(client);
                            this.setSentiment(client);
                            this.getClient(client);
                        });
                    }

                }
            })
        },

        getClient(client) {
            this.$http.get('/clients/'+client.client)
            .then(response => {
                if(response && response.data) {

                    client = {
                        ...client,
                        ...response.data
                    }

                    this.clients.push(client);
                }
            })
        },

        getSupervisions() {
            this.$http.get('/supervisions')
            .then(response => {
                if(response && response.data) {
                    this.supervisions = response.data;

                    if(this.supervisions.length > 0) {
                        this.supervisions.forEach(supervision => {
                            this.getClients(supervision);
                        });
                    }
                }
            })
        },

        startVideoCall() {
        },

        makePhoneCall() {
        },

        sendMessage() {
        },

        getSentimentEmoji(sentiment) {
            const emojis = {
                'very_positive': '😊',
                'positive': '🙂',
                'neutral': '😐',
                'negative': '🙁',
                'very_negative': '😠',
                'default': '🤔'
            };
            return emojis[sentiment] || emojis.default;
        },

        getSentimentText(sentiment) {
            const texts = {
                'very_positive': 'Very Positive',
                'positive': 'Positive',
                'neutral': 'Neutral',
                'negative': 'Negative',
                'very_negative': 'Very Negative',
                'default': 'Analyzing...'
            };
            return texts[sentiment] || texts.default;
        },

        getSentimentClass(sentiment) {
            const classes = {
                'very_positive': 'text-success',
                'positive': 'text-success',
                'neutral': 'text-warning',
                'negative': 'text-danger',
                'very_negative': 'text-danger',
                'default': 'text-muted'
            };
            return classes[sentiment] || classes.default;
        },

        initializeTimeline() {
            if (!this.currentClient || !this.geolocations) return;

            // Filter and sort geolocations for this client
            this.timelineData = this.geolocations
                .filter(geo => geo.client_uuid === this.currentClient.uuid)
                .sort((a, b) => new Date(a.geo_data.created_at) - new Date(b.geo_data.created_at));

            this.currentTimeIndex = this.timelineData.length - 1; // Start at most recent
            this.updateTimelineDisplay();
        },

        clearTimeline() {
            this.stopPlayback();
            this.timelineData = [];
            this.currentTimeIndex = 0;
            this.clearMap();
        },

        clearMap() {
            if (this.markers) {
                this.markers.clearLayers();
            }
        },

        updateTimelineDisplay() {
            if (!this.map) return;
            
            this.clearMap();
            if (this.timelineData.length === 0) return;

            const currentGeo = this.timelineData[this.currentTimeIndex];
            if (!currentGeo.geo_data) return;

            // Calculate radius in feet
            let radius = 328; // Default radius in feet (100m)
            if (currentGeo.geo_data.accuracy && currentGeo.geo_data.accuracy < 1000) {
                radius = currentGeo.geo_data.accuracy * 3.28084;
            }

            try {
                // Create circle
                const circle = window.L.circle([currentGeo.geo_data.latitude, currentGeo.geo_data.longitude], {
                    color: '#007AFF',
                    fillColor: '#007AFF',
                    fillOpacity: 0.15,
                    radius: radius,
                    weight: 1,
                    className: 'find-my-circle'
                });

                const popup = window.L.popup({
                    maxWidth: 300,
                    className: 'custom-popup'
                }).setContent(`
                    <div class="p-2">
                        <strong>Check-in Details</strong><br>
                        <small class="text-muted">${this.formatDate(currentGeo.geo_data.created_at)}</small><br>
                        <span class="badge ${this.getZoneStatus(currentGeo.geo_data).includes('danger') ? 'bg-danger' : 
                                          this.getZoneStatus(currentGeo.geo_data).includes('success') ? 'bg-success' : 'bg-warning'}">
                            ${this.getZoneStatus(currentGeo.geo_data).replace(/<[^>]*>/g, '')}
                        </span>
                        ${currentGeo.geo_data.accuracy ? `<br><small class="text-muted">Accuracy: ${Math.round(currentGeo.geo_data.accuracy * 3.28084)} feet</small>` : ''}
                    </div>
                `);

                circle.bindPopup(popup);
                circle.addTo(this.map);

                // Center map on current location
                this.map.setView([currentGeo.geo_data.latitude, currentGeo.geo_data.longitude], this.map.getZoom());
            } catch (error) {
                console.error('Error updating timeline display:', error);
            }
        },

        togglePlayback() {
            if (this.isPlaying) {
                this.stopPlayback();
            } else {
                this.startPlayback();
            }
        },

        startPlayback() {
            if (this.timelineData.length === 0) return;
            this.isPlaying = true;
            this.playbackInterval = setInterval(() => {
                if (this.currentTimeIndex < this.timelineData.length - 1) {
                    this.currentTimeIndex++;
                    this.updateTimelineDisplay();
                } else {
                    this.stopPlayback();
                }
            }, this.playbackSpeed);
        },

        stopPlayback() {
            this.isPlaying = false;
            if (this.playbackInterval) {
                clearInterval(this.playbackInterval);
                this.playbackInterval = null;
            }
        },

        stepForward() {
            if (this.currentTimeIndex < this.timelineData.length - 1) {
                this.currentTimeIndex++;
                this.updateTimelineDisplay();
            }
        },

        stepBackward() {
            if (this.currentTimeIndex > 0) {
                this.currentTimeIndex--;
                this.updateTimelineDisplay();
            }
        },

        setTimelinePosition(index) {
            if (index >= 0 && index < this.timelineData.length) {
                this.currentTimeIndex = index;
                this.updateTimelineDisplay();
            }
        },

        scheduleOfficeVisit() {
            alert('Schedule Office Visit action triggered!');
        },
        scheduleDrugTest() {
            alert('Schedule Drug Test action triggered!');
        },
        scheduleVirtualVisit() {
            alert('Schedule Virtual Visit action triggered!');
        },

        getPortal() {
            this.$http.get('/portals?slim=true&limit=1&sort=default&sortDirection=desc&fields=name,link,domain,alias')
            .then(response => {
                if(response && response.data && response.data.length > 0) {
                    this.portal = response.data[0];
                }
            })
            
        }
    }
}
</script>

<style scoped>
.card {
    border: none;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    transition: transform 0.2s ease;
}

.card:hover {
    transform: translateY(-2px);
}

.progress {
    background-color: #e9ecef;
    border-radius: 0.25rem;
}

.table th {
    font-weight: 600;
    color: #495057;
}

.badge {
    font-weight: 500;
    padding: 0.5em 0.75em;
}

.input-group {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.mugshot-placeholder {
    width: 150px;
    height: 200px;
    margin: 0 auto;
    background: #e9ecef;
    border: 2px solid #dee2e6;
    position: relative;
    overflow: hidden;
}

.height-strip {
    position: absolute;
    left: 0;
    right: 0;
    height: 20px;
    background: repeating-linear-gradient(
        45deg,
        #dee2e6,
        #dee2e6 10px,
        #e9ecef 10px,
        #e9ecef 20px
    );
}

.placeholder-content {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 3rem;
    color: #adb5bd;
}

.placeholder-content i {
    display: block;
    text-align: center;
}

.map-lg {
    height: 400px;
    transition: all 0.3s ease;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    position: relative;
}

.map-fullscreen {
    position: fixed !important;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw !important;
    height: 100vh !important;
    z-index: 9999;
    margin: 0;
    border-radius: 0;
    background: #fff;
    transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
    box-shadow: none;
}

.exit-fullscreen-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10001;
    background: rgba(255,255,255,0.9);
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    font-weight: 600;
    padding: 0.5rem 1rem;
    border: none;
    outline: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.exit-fullscreen-btn i {
    font-size: 1.2rem;
}

/* Custom cluster styling */
.custom-cluster {
    background-color: rgba(255, 0, 0, 0.7);
    border: 2px solid rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    color: white;
    font-weight: bold;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 15px rgba(255, 0, 0, 0.6);
}

.cluster-marker {
    font-size: 14px;
    text-shadow: 0 0 2px rgba(0, 0, 0, 0.8);
}

/* Night mode effect */
.night-mode {
    filter: brightness(0.9) contrast(1.1) saturate(1.1);
}

/* Custom control styling */
.leaflet-control-layers {
    background-color: rgba(0, 0, 0, 0.5) !important;
    color: white !important;
    border: none !important;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.3) !important;
}

.leaflet-control-layers label {
    color: white !important;
}

.leaflet-control-layers-toggle {
    background-color: rgba(0, 0, 0, 0.5) !important;
    border: none !important;
}

.leaflet-control-zoom {
    border: none !important;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.3) !important;
}

.leaflet-control-zoom a {
    background-color: rgba(0, 0, 0, 0.5) !important;
    color: white !important;
    border: none !important;
}

.leaflet-control-zoom a:hover {
    background-color: rgba(255, 255, 255, 0.3) !important;
}

/* Scale control styling */
.leaflet-control-scale {
    background-color: rgba(0, 0, 0, 0.5) !important;
    color: white !important;
    border: none !important;
    padding: 2px 5px !important;
    border-radius: 4px !important;
}

/* Custom marker styling */
.map-icon {
    background: transparent;
    border: none;
    filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.8));
}

/* Custom circle styling */
.location-circle {
    stroke: #ff0000;
    stroke-width: 2px;
    stroke-opacity: 1;
    fill: #ff0000;
    fill-opacity: 0.3;
}

/* Custom popup styling */
.custom-popup {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(8px);
}

.custom-popup .leaflet-popup-content-wrapper {
    background: transparent;
    box-shadow: none;
    padding: 0;
}

.custom-popup .leaflet-popup-tip {
    background: rgba(255, 255, 255, 0.95);
}

/* Find My style circle */
.find-my-circle {
    stroke: #007AFF;
    stroke-width: 1px;
    stroke-opacity: 0.8;
    fill: #007AFF;
    fill-opacity: 0.15;
    transition: all 0.3s ease;
}

.find-my-circle:hover {
    fill-opacity: 0.25;
    stroke-opacity: 1;
}

.timeline-slider {
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    padding: 0 10px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    backdrop-filter: blur(4px);
}

.timeline-slider input[type="range"] {
    width: 100%;
}

.timeline-slider small {
    display: block;
    text-align: center;
    padding: 4px 0;
}

.btn-group {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-group .btn {
    padding: 0.375rem 0.75rem;
}

.score-card {
    background: #fff;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: transform 0.2s ease;
}

.score-card:hover {
    transform: translateY(-2px);
}

.score-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.score-value {
    font-size: 2rem;
    font-weight: 600;
    line-height: 1;
}

.score-description {
    font-size: 0.875rem;
    color: #6c757d;
    margin-top: 0.5rem;
}

.sentiment-card {
    background: #fff;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.sentiment-display {
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.25rem;
    padding: 0.75rem;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.03);
}

.sentiment-display i {
    font-size: 1.5rem;
}

.sentiment-text {
    font-weight: 500;
}

.progress {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    overflow: hidden;
}

.progress-bar {
    transition: width 0.6s ease;
}

.score-bubble {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 70px;
    height: 70px;
    font-size: 2.2rem;
    font-weight: 700;
    border-radius: 50%;
    margin: 0 auto 0.5rem auto;
    box-shadow: 0 2px 8px rgba(0,0,0,0.07);
    background: #f8f9fa;
    border: 3px solid #e9ecef;
    color: #212529;
    transition: background 0.2s, color 0.2s;
}
.bg-danger.score-bubble, .score-bubble.bg-danger {
    background: #dc3545;
    color: #fff;
    border-color: #dc3545;
}
.bg-warning.score-bubble, .score-bubble.bg-warning {
    background: #ffc107;
    color: #212529;
    border-color: #ffc107;
}
.bg-success.score-bubble, .score-bubble.bg-success {
    background: #28a745;
    color: #fff;
    border-color: #28a745;
}

.sentiment-emoji {
    font-size: 2.5rem;
    margin-right: 0.5rem;
    vertical-align: middle;
}
.sentiment-display {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    background: #f8f9fa;
    min-width: 200px;
    margin: 0 auto;
}

.activity-timeline {
    position: relative;
    margin-left: 1.5rem;
    padding-left: 1.5rem;
    border-left: 2px solid #e9ecef;
}
.timeline-item {
    position: relative;
    margin-bottom: 2rem;
    min-height: 60px;
    display: flex;
    align-items: flex-start;
}
.timeline-dot {
    position: absolute;
    left: -1.7rem;
    top: 0.2rem;
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    color: #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.07);
    border: 3px solid #fff;
    z-index: 2;
}
.timeline-content {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1rem 1.5rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.04);
    width: 100%;
}
.timeline-extra {
    margin-top: 0.5rem;
    font-size: 0.97rem;
    color: #444;
}
</style>